import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../../../../components/common/BriaButton/BriaButton.tsx";
import { useAppStore } from "../../../../../../../hooks/useStores.tsx";
import InputLayout from "../../../../../../../layout/InputLayout/InputLayout.tsx";
import { TgModel } from "../../../../store/tg-model-store.tsx";
import CompareModel from "./CompareModel";
import styles from "./CompareModels.module.scss";

const CompareModels = () => {
	const { t } = useTranslation("tg", { keyPrefix: "models.viewer.evaluation.config.compareModels" });
	const { tgStore } = useAppStore();
	const { tgProjectStore, tgModelPlaygroundStore } = tgStore;
	const { compareModels } = tgModelPlaygroundStore;

	const isComparable =
		(!tgProjectStore.loadingProjectModels && tgProjectStore.paginatedModels.total > 1) ||
		(tgProjectStore.paginatedModels.items[0]?.checkpoint_steps &&
			tgProjectStore.paginatedModels.items[0].checkpoint_steps.length > 1);
	const canAddMore = (compareModels?.length ?? 0) < 3;

	const handleAddModel = () => {
		if (canAddMore) {
			tgModelPlaygroundStore.addCompareModel();
		}
	};

	const handleRemoveModel = (index: number) => {
		tgModelPlaygroundStore.removeCompareModel(index);
	};

	const handleModelChange = (index: number, model: TgModel | undefined) => {
		tgModelPlaygroundStore.updateCompareModel(index, model);
	};

	return (
		<Box className={styles.compareModelsWrapper}>
			<InputLayout
				label={t("label")}
				labelClassName={styles.labelClassName}
				description={t("description")}
				actionButton={
					isComparable &&
					!!compareModels?.length && (
						<BriaButton
							buttonType="textSmall"
							className={styles.addButton}
							onClick={handleAddModel}
							disabled={!canAddMore}
							disabledTooltip={t("addButton.disabledTooltip")}
						>
							{t("addButton.label")}
						</BriaButton>
					)
				}
			>
				<Box className={styles.compareModels}>
					{!isComparable ? (
						<Typography className={styles.notComparableText}>{t("notComparableText")}</Typography>
					) : !compareModels?.length ? (
						<BriaButton buttonType="textMedium" className={styles.bigAddButton} onClick={handleAddModel}>
							{t("addButton.label")}
						</BriaButton>
					) : (
						compareModels?.map((model, index) => (
							<CompareModel
								key={index}
								model={model}
								index={index}
								onRemove={() => handleRemoveModel(index)}
								onModelChange={(model) => handleModelChange(index, model)}
							/>
						))
					)}
				</Box>
			</InputLayout>
		</Box>
	);
};

export default observer(CompareModels);
